import React, { memo, useEffect, useState } from "react";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../styles/AutocompleteStyles";
import { TextField } from "@mui/material";
import { responseType } from "../utils/formTypes";
import { useLazyQuery } from "@apollo/client";

import useUrlParams from "../customHooks/useUrlParams";
import { FETCH_MORE_DATA, ROWS_PER_PAGE, debouncedDelay } from "../utils/constants";
import { OrderDirection } from "../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../utils/UtilFunctions";

import { GetItemsDetailsForAutocomplete } from "../components/modules/asset/masters/items/queries/queries";
import {
  AtsStckItemDetailsEdges,
  AtsStckItemDetailsOrderField,
  GetItemsDetailsResult,
  GetItemsDetailsVars,
} from "../components/modules/asset/masters/items/queries/types";
import { itmDetailsType } from "../components/modules/asset/purchase/material/statesProvider/materialContext";
import { ItemDetailsQueryType } from "../utils/Enum.types";
import { useDebounce } from "../customHooks/useDebounce";

export interface inputType {
  query_type: ItemDetailsQueryType;
  stckCategoryID: number;
  stckGroupID: number;
  stckManufacturerID: number;
  stckUomID: number;
  stckSubCategoryID: number;
  itemID: number;
  ldgrID: number;
  poleNum: number;
  empID: number;
}
export interface queryInput {
  customerID: number;
  companyID: number;
  subsidiaryID: number;
  junctionID: number;
  siteID: number;
}

export interface ItemsAutocompleteProps {
  onChange: (newValue: itmDetailsType) => void;
  value: number;
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  input: inputType;
  queryInput: queryInput;
  autoFocus?: boolean;
  label?: string;
}
const ItemsDetailsAutocomplete = ({
  onChange,
  onKeyDown,
  value,
  autoFocus,
  label,
  input,
  queryInput,
}: ItemsAutocompleteProps) => {
  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState("");
  const debouncedSearch = useDebounce(searchData, debouncedDelay);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [items, setItems] = useState<AtsStckItemDetailsEdges[]>([]);
  const [itemOptions, setItemOptions] = useState<itmDetailsType[]>([]);
  const [autoValue, setAutoValue] = useState<itmDetailsType | null>(null);

  const [GetItems, { data, loading, error, fetchMore }] = useLazyQuery<
    GetItemsDetailsResult,
    GetItemsDetailsVars
  >(GetItemsDetailsForAutocomplete);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemsDetails.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
        setItemOptions(
          updatedNewData.map(({ node }) => ({
            label: node.prodSerialNo,
            value: node.id,
            itemName: node.item.desc,
            poleNum: node.poleNum,
            address: node.address,
          }))
        );
      } else {
        setItems(newData);
        setItemOptions(
          newData.map(({ node }) => ({
            label: node.prodSerialNo,
            value: node.id,
            itemName: node.item.desc,
            poleNum: node.poleNum,
            address: node.address,
          }))
        );
      }
      setEndCursor(data.GetItemsDetails.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id) {
          GetItems({
            variables: {
              ...queryInput,
              input,
              after: null,
              first: ROWS_PER_PAGE,
              name: debouncedSearch,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckItemDetailsOrderField.CREATED_AT,
              },
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [customer_id, GetItems, debouncedSearch, input, queryInput]);

  useEffect(() => {
    if (value) {
      const foundValue = itemOptions.find(
        ({ value: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, itemOptions]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetItemsDetails.edges;
            const pageInfo = fetchMoreResult.GetItemsDetails.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetItemsDetails.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetItemsDetails: {
                edges: [...items, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetItemsDetails.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete = label ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={itemOptions}
      onKeyDown={onKeyDown}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      getOptionLabel={(option) => {
        if (
          (option as itmDetailsType).itemName &&
          (option as itmDetailsType).address
        ) {
          return `${(option as itmDetailsType).label} (${
            (option as itmDetailsType).itemName
          }) (${(option as itmDetailsType).address})`;
        } else if ((option as itmDetailsType).itemName) {
          return `${(option as itmDetailsType).label} (${
            (option as itmDetailsType).itemName
          })`;
        } else {
          return `${(option as itmDetailsType).label}`;
        }
      }}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        const uom = items.find(
          ({ node }) =>
            newValue &&
            Number(node.id) === Number((newValue as responseType).value)
        );
        onChange(newValue as itmDetailsType);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          autoFocus={autoFocus}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      )}
    />
  );
};

export default memo(ItemsDetailsAutocomplete);
